import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../index';
import { Tag, SortOptions, TimeFilterOptions } from '../types';

interface UseTagsOptions extends SortOptions, TimeFilterOptions {
  type?: 'manual' | 'system' | 'recent' | 'assignable';
}

export const useTags = (options: UseTagsOptions = {}) => {
  const [data, setData] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      
      if (options.sortColumn) {
        params.sortColumn = options.sortColumn;
        params.sortDirection = options.sortDirection || 'asc';
      }
      
      if (options.showAll) {
        params.showAll = 'true';
      }

      let endpoint = '/api/tags';
      if (options.type) {
        endpoint = `/api/tags/${options.type}`;
      }

      const result = await apiClient.get<Tag[]>(endpoint, params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tags');
    } finally {
      setLoading(false);
    }
  }, [options.type, options.sortColumn, options.sortDirection, options.showAll]);

  const createTag = useCallback(async (name: string) => {
    const result = await apiClient.post<Tag>('/api/tags', { name });
    await fetchTags();
    return result;
  }, [fetchTags]);

  const updateTag = useCallback(async (id: number, name: string) => {
    const result = await apiClient.put<Tag>(`/api/tags/${id}`, { name });
    await fetchTags();
    return result;
  }, [fetchTags]);

  const deleteTag = useCallback(async (id: number) => {
    await apiClient.delete(`/api/tags/${id}`);
    await fetchTags();
  }, [fetchTags]);

  const uploadCsv = useCallback(async (file: File) => {
    const { getCsvChunkSize } = await import('../../config/settings');
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0];
    const dataRows = lines.slice(1);
    
    const chunkSize = await getCsvChunkSize();
    const chunks = [];
    for (let i = 0; i < dataRows.length; i += chunkSize) {
      chunks.push(dataRows.slice(i, i + chunkSize));
    }
    
    const uploadPromises = chunks.map((chunk, index) => {
      const csvContent = [headers, ...chunk].join('\n');
      const chunkFile = new File([csvContent], `chunk_${index}.csv`, { type: 'text/csv' });
      const formData = new FormData();
      formData.append('file', chunkFile);
      return apiClient.upload('/api/tags/upload-csv', formData);
    });
    
    const results = await Promise.allSettled(uploadPromises);
    await fetchTags();
    
    // Aggregate results from all successful chunks
    const successfulResults = results.filter(r => r.status === 'fulfilled').map(r => (r as any).value);
    const allUploadedTags = successfulResults.flatMap(result => result.uploadedTags || []);
    const uniqueUploadedTags = Array.from(new Set(allUploadedTags));
    
    // Refresh counts for all uploaded tags in parallel
    if (uniqueUploadedTags.length > 0) {
      await Promise.all(
        uniqueUploadedTags.map((tagName: string) => 
          apiClient.post(`/api/tags/refresh-count/${encodeURIComponent(tagName)}`)
            .catch(err => console.error(`Failed to refresh count for tag ${tagName}:`, err))
        )
      );
    }
    
    // Aggregate stats from all chunks
    const totalRows = successfulResults.reduce((sum, result) => sum + (result.stats?.totalRows || 0), 0);
    const subscribersFound = successfulResults.reduce((sum, result) => sum + (result.stats?.subscribersFound || 0), 0);
    const subscribersNotFound = successfulResults.reduce((sum, result) => sum + (result.stats?.subscribersNotFound || 0), 0);
    const uniqueTags = Array.from(new Set(allUploadedTags)).length;
    const totalTagsAssigned = successfulResults.reduce((sum, result) => sum + (result.stats?.totalTagsAssigned || 0), 0);
    const successfulInserts = successfulResults.reduce((sum, result) => sum + (result.stats?.successfulInserts || 0), 0);
    const duplicates = successfulResults.reduce((sum, result) => sum + (result.stats?.duplicates || 0), 0);
    
    return {
      totalChunks: chunks.length,
      successful: results.filter(r => r.status === 'fulfilled').length,
      failed: results.filter(r => r.status === 'rejected').length,
      message: successfulResults[0]?.message || 'Upload completed',
      stats: {
        totalRows,
        subscribersFound,
        subscribersNotFound,
        uniqueTags,
        totalTagsAssigned,
        successfulInserts,
        duplicates
      },
      uploadedTags: uniqueUploadedTags,
      results
    };
  }, [fetchTags]);

  const refreshCounts = useCallback(async () => {
    await apiClient.post('/api/tags/refresh-counts');
    await fetchTags();
  }, [fetchTags]);

  const refreshSingleCount = useCallback(async (tagName: string) => {
    await apiClient.post(`/api/tags/refresh-count/${encodeURIComponent(tagName)}`);
    await fetchTags();
  }, [fetchTags]);

  const bulkDeleteTags = useCallback(async (tagIds: number[]) => {
    await Promise.all(tagIds.map(id => apiClient.delete(`/api/tags/${id}`)));
    await fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return {
    data,
    loading,
    error,
    refetch: fetchTags,
    createTag,
    updateTag,
    deleteTag,
    uploadCsv,
    refreshCounts,
    refreshSingleCount,
    bulkDeleteTags,
  };
};